import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControl, InputLabel, Select, MenuItem,
  Typography, Divider, Box, CircularProgress, Alert,
} from '@mui/material';
import { LinkOutlined } from '@mui/icons-material';

export const LinkDialog = ({ open, onClose, gameEntry, bracket, onSave }) => {
  const [toStageId, setToStageId] = useState('');
  const [toGameEntryId, setToGameEntryId] = useState('');
  const [slot, setSlot] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (gameEntry?.winnerFeedsInto) {
      setToStageId(gameEntry.winnerFeedsInto.stageId || '');
      setToGameEntryId(gameEntry.winnerFeedsInto.gameEntryId || '');
      setSlot(gameEntry.winnerFeedsInto.slot || 1);
    } else {
      setToStageId(''); setToGameEntryId(''); setSlot(1);
    }
    setError('');
  }, [gameEntry, open]);

  const selectedStage = bracket?.stages?.find(s => s._id === toStageId);

  const handleSave = async () => {
    setLoading(true); setError('');
    try {
      await onSave({ stageId: toStageId, gameEntryId: toGameEntryId, slot: Number(slot) });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save link');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    setLoading(true); setError('');
    try {
      await onSave({ clear: true });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to clear link');
    } finally {
      setLoading(false);
    }
  };

  if (!gameEntry || !bracket) return null;

return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinkOutlined color="secondary" />
        <Typography fontWeight="bold">Lier le vainqueur à l'étape suivante</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="caption" color="text.secondary">
          Le vainqueur de ce match sera placé en tant qu'entité sélectionnée dans le match cible.
        </Typography>

        <FormControl fullWidth size="small">
          <InputLabel>Étape cible</InputLabel>
          <Select value={toStageId} label="Étape cible" onChange={e => { setToStageId(e.target.value); setToGameEntryId(''); }}>
            {bracket.stages.map(s => (
              <MenuItem key={s._id} value={s._id}>{s.title}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" disabled={!toStageId}>
          <InputLabel>Match cible</InputLabel>
          <Select value={toGameEntryId} label="Match cible" onChange={e => setToGameEntryId(e.target.value)}>
            {(selectedStage?.games || []).map((g, i) => {
              const e1 = g.entity1Id?.name || 'À définir';
              const e2 = g.entity2Id?.name || 'À définir';
              return <MenuItem key={g._id} value={g._id}>Match {i + 1} : {e1} vs {e2}</MenuItem>;
            })}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>Le vainqueur devient…</InputLabel>
          <Select value={slot} label="Le vainqueur devient…" onChange={e => setSlot(e.target.value)}>
            <MenuItem value={1}>Équipe 1 (gauche/haut)</MenuItem>
            <MenuItem value={2}>Équipe 2 (droite/bas)</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        {gameEntry?.winnerFeedsInto?.gameEntryId && (
          <Button onClick={handleClear} color="error" variant="outlined" disabled={loading} sx={{ mr: 'auto' }}>
            Supprimer le lien
          </Button>
        )}
        <Button onClick={onClose} variant="outlined">Annuler</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading || !toStageId || !toGameEntryId}
          startIcon={loading ? <CircularProgress size={14} /> : <LinkOutlined />}>
          Enregistrer le lien
        </Button>
      </DialogActions>
    </Dialog>
  );
};
